import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { FileText, Loader2, Send } from "lucide-react";

const fmt = (tpl, ctx) => (tpl || "").replace(/\{(\w+)\}/g, (_, k) => (ctx[k] ?? "").toString());

const buildDocumenti = (notes) =>
  (notes || [])
    .map((n) => {
      const h = n.header || {};
      const id = h.document_id || h.delivery_note_id || "";
      return `- ${id} — ${h.ragione_sociale || ""} (${h.nazione || ""})`;
    })
    .join("\n");

const fileTag = (run, mandante) => {
  const ids = (run?.notes || []).map((n) => n.header?.delivery_note_id).filter(Boolean);
  const tag = ids.length === 0 ? "ordine" : ids.length <= 3 ? ids.join("-") : `${ids.length}consegne`;
  return `Importazione_${mandante || "D02"}_${tag}.xlsx`;
};

export const SendDialog = ({ run, settings, open, onOpenChange, onSent }) => {
  const [sending, setSending] = useState(false);
  const [mail, setMail] = useState({ recipients: "", cc: "", subject: "", body: "" });

  useEffect(() => {
    if (!open || !run || !settings) return;
    const first = run.notes?.[0]?.header || {};
    const ctx = {
      delivery_note_id: first.delivery_note_id || "",
      document_id: first.document_id || "",
      cliente: first.ragione_sociale || "",
      ragione_sociale: first.ragione_sociale || "",
      nazione: first.nazione || "",
      citta: first.citta || "",
      cap: first.cap || "",
      via: first.via || "",
      provincia: first.provincia || "",
      mandante: settings.mandante || "D02",
      tipo_ordine: settings.tipo_ordine || "",
      causale: settings.causale || "",
      magazzino: settings.magazzino || "",
      documenti: buildDocumenti(run.notes),
      num_documenti: String(run.notes?.length || 0),
    };
    setMail({
      recipients: (run.recipients?.length ? run.recipients : settings.recipients || []).join(", "),
      cc: (run.cc?.length ? run.cc : settings.cc_recipients || []).join(", "),
      subject: fmt(settings.email_subject, ctx),
      body: fmt(settings.email_body, ctx),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, run?.id]);

  const doSend = async () => {
    const recipients = mail.recipients.split(",").map((r) => r.trim()).filter(Boolean);
    const cc = mail.cc.split(",").map((r) => r.trim()).filter(Boolean);
    if (!recipients.length) {
      toast.error("Inserisci almeno un destinatario");
      return;
    }
    setSending(true);
    try {
      const res = await api.post(`/runs/${run.id}/send`, { recipients, cc, subject: mail.subject, body: mail.body });
      toast.success(`Email inviata a ${res.data.recipients.length} destinatari${res.data.cc?.length ? ` (+${res.data.cc.length} CC)` : ""}`);
      onOpenChange(false);
      onSent && onSent(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invio email fallito");
    } finally {
      setSending(false);
    }
  };

  const fname = fileTag(run, settings?.mandante);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display">Invia file ordini via Email</DialogTitle>
          <DialogDescription>
            Un unico file MM con {run?.notes?.length || 0} consegne partirà dal tuo indirizzo Gmail.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-500">Destinatari (separati da virgola)</Label>
            <Input data-testid="send-recipients-input" value={mail.recipients} onChange={(e) => setMail((m) => ({ ...m, recipients: e.target.value }))} placeholder="ordini@mmoperations.com" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-500">CC (separati da virgola)</Label>
            <Input data-testid="send-cc-input" value={mail.cc} onChange={(e) => setMail((m) => ({ ...m, cc: e.target.value }))} placeholder="copia@azienda.com" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-500">Oggetto</Label>
            <Input data-testid="send-subject-input" value={mail.subject} onChange={(e) => setMail((m) => ({ ...m, subject: e.target.value }))} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-500">Messaggio</Label>
            <Textarea data-testid="send-body-input" rows={7} value={mail.body} onChange={(e) => setMail((m) => ({ ...m, body: e.target.value }))} className="font-mono-data text-xs" />
          </div>
          <div className="flex items-center gap-2 text-sm text-slate-500 bg-slate-50 dark:bg-slate-800/50 rounded-lg px-3 py-2">
            <FileText className="h-4 w-4 text-blue-600" /> Allegato: {fname}
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button data-testid="confirm-send-email-button" onClick={doSend} disabled={sending} className="gap-2 bg-blue-600 hover:bg-blue-700">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Invia
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
